<template>
  <section class="brands-section">
    <div class="container">
      <div class="section-header">
        <h2 class="section-title">
          {{ $t('home.trustedBrands') }}
        </h2>
        <!--        <p class="section-subtitle">与全球知名品牌合作，共同构建防伪生态系统</p>-->
      </div>

      <div class="brands-container">
        <div class="brands-grid">
          <div
            v-for="(brand, index) in brands"
            :key="brand.id"
            class="brand-card"
            :class="{ 'featured': brand.featured }"
            :style="{ animationDelay: `${index * 0.1}s` }"
          >
            <div class="brand-logo-container">
              <!--              <img-->
              <!--                :src="brand.logo"-->
              <!--                :alt="brand.name"-->
              <!--                class="brand-logo"-->
              <!--                :class="{ 'monochrome': !brand.featured }"-->
              <!--                loading="lazy"-->
              <!--                @error="handleImageError"-->
              <!--              >-->
              <img
                :src="brand.logo"
                :alt="brand.name"
                class="brand-logo"
                loading="lazy"
                @error="handleImageError"
              >

              <div
                v-if="brand.featured"
                class="featured-badge"
              >
                <van-icon name="fire" />
                推荐品牌
              </div>
            </div>

            <div
              v-if="brand.featured"
              class="brand-info"
            >
              <h3 class="brand-name">
                {{ brand.name }}
              </h3>
              <p class="brand-desc">
                {{ brand.description }}
              </p>

              <div class="brand-stats">
                <div class="stat">
                  <div class="stat-number">
                    {{ brand.productCount }}+
                  </div>
                  <div class="stat-label">
                    产品
                  </div>
                </div>
                <div class="stat">
                  <div class="stat-number">
                    {{ brand.verificationCount }}+
                  </div>
                  <div class="stat-label">
                    验证
                  </div>
                </div>
              </div>

              <van-button
                size="small"
                class="brand-button"
                @click="handleBrandClick(brand)"
              >
                了解更多
              </van-button>
            </div>
          </div>
        </div>
      </div>

      <div class="section-footer">
        <van-button
          type="primary"
          size="large"
          class="view-all-button"
          @click="goToBrandsPage"
        >
          {{$t('home.viewAllBrands')}}
          <van-icon
            name="arrow"
            class="button-arrow"
          />
        </van-button>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Brand, queryBrandList } from '@/api/brand'
import { PaginationParams } from '@/api/base'
import { useI18n } from 'vue-i18n'
export default {
  setup () {
    const router = useRouter()
    const brands = ref<Brand[]>([])
    function getBrandList () {
      // 模拟异步请求数据
      const params: PaginationParams = {
        page: 1,
        size: 12
      }
      queryBrandList(params).then(res => {
        brands.value = res.list
      })
    }
    const { locale } = useI18n()
    watch(locale, () => {
      getBrandList()
    })

    const showScrollIndicator = ref(true)

    const handleImageError = (event: Event) => {
      const target = event.target as HTMLImageElement
      target.src = 'https://via.placeholder.com/200x100/0A1E3C/FFFFFF?text=Brand+Logo'
    }

    const handleBrandClick = (brand: Brand) => {
      if (brand.website) {
        window.open(brand.website, '_blank')
      } else {
        router.push(`/brands/${brand.id}`)
      }
    }

    const goToBrandsPage = () => {
      router.push('/brand')
    }

    // 检测滚动以隐藏指示器
    let scrollTimeout: number

    const checkScroll = () => {
      clearTimeout(scrollTimeout)
      showScrollIndicator.value = false

      scrollTimeout = setTimeout(() => {
        showScrollIndicator.value = true
      }, 3000)
    }

    onMounted(() => {
      getBrandList()
      const container = document.querySelector('.brands-container')
      if (container) {
        container.addEventListener('scroll', checkScroll)
      }
    })

    onUnmounted(() => {
      const container = document.querySelector('.brands-container')
      if (container) {
        container.removeEventListener('scroll', checkScroll)
      }
      clearTimeout(scrollTimeout)
    })
    return {
      brands,
      showScrollIndicator,
      handleImageError,
      handleBrandClick,
      goToBrandsPage
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.brands-section {
  padding: $spacing-xxl 0;
  background: linear-gradient(to bottom, #ffffff 0%, #f8fafc 100%);
  position: relative;
  overflow: hidden;
}

.section-header {
  text-align: center;
  margin-bottom: $spacing-xxl;
}

.section-title {
  font-size: $font-size-xxl;
  font-weight: 700;
  margin-bottom: $spacing-md;
  color: $primary-blue;
  position: relative;

  &::after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background: $accent-gold;
    margin: $spacing-md auto;
    border-radius: 2px;
  }
}

.section-subtitle {
  font-size: $font-size-lg;
  color: $text-light;
  max-width: 600px;
  margin: 0 auto;
}

.brands-container {
  position: relative;
  margin-bottom: $spacing-xl;
}

.brands-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: $spacing-lg;
  padding: $spacing-sm;
}

.brand-card {
  background: $white;
  border-radius: $border-radius-lg;
  box-shadow: $card-shadow;
  padding: $spacing-lg;
  transition: all $transition-normal;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  opacity: 0;
  animation: fadeInUp 0.6s ease forwards;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 25px rgba(10, 30, 60, 0.15);
  }

  &.featured {
    grid-column: span 2;
    flex-direction: row;
    align-items: center;
    gap: $spacing-lg;

    .brand-logo-container {
      flex-shrink: 0;
    }

    .brand-info {
      flex-grow: 1;
    }
  }
}

.brand-logo-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: $spacing-md;
  height: 100px;
}

.brand-logo {
  max-width: 100%;
  max-height: 80px;
  object-fit: contain;
  transition: all $transition-normal;

  &.monochrome {
    filter: grayscale(100%);
    opacity: 0.7;

    .brand-card:hover & {
      filter: grayscale(0);
      opacity: 1;
    }
  }
}

.featured-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background: linear-gradient(135deg, $accent-gold 0%, darken($accent-gold, 10%) 100%);
  color: $white;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: $font-size-xs;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 4px 8px rgba(212, 175, 55, 0.3);
}

.brand-info {
  text-align: center;
}

.brand-name {
  font-size: $font-size-lg;
  font-weight: 600;
  margin-bottom: $spacing-sm;
  color: $primary-blue;
}

.brand-desc {
  color: $text-light;
  margin-bottom: $spacing-lg;
  line-height: 1.5;
  font-size: $font-size-sm;
}

.brand-stats {
  display: flex;
  justify-content: center;
  gap: $spacing-xl;
  margin-bottom: $spacing-lg;
}

.stat {
  text-align: center;
}

.stat-number {
  font-size: $font-size-xl;
  font-weight: 700;
  color: $accent-gold;
  margin-bottom: 2px;
}

.stat-label {
  font-size: $font-size-xs;
  color: $text-light;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.brand-button {
  background: linear-gradient(135deg, $primary-blue 0%, lighten($primary-blue, 10%) 100%);
  border: none;
  color: $white;
  font-weight: 500;

  &:hover {
    background: linear-gradient(135deg, lighten($primary-blue, 5%) 0%, lighten($primary-blue, 15%) 100%);
  }
}

.brands-scroll-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $spacing-xs;
  color: $text-light;
  font-size: $font-size-sm;
  margin-top: $spacing-lg;
  opacity: 1;
  transition: opacity $transition-normal;

  &:not(:show) {
    opacity: 0;
  }
}

.scroll-arrow {
  animation: bounce 2s infinite;
}

.section-footer {
  text-align: center;
  margin-top: $spacing-xl;
}

.view-all-button {
  background: linear-gradient(135deg, $accent-gold 0%, darken($accent-gold, 5%) 100%);
  border: none;
  font-weight: 600;
  padding: 12px 32px;

  &:hover {
    background: linear-gradient(135deg, lighten($accent-gold, 5%) 0%, $accent-gold 100%);
  }
}

.button-arrow {
  margin-left: $spacing-xs;
  transition: transform $transition-normal;

  .view-all-button:hover & {
    transform: translateX(4px);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateX(0);
  }
  40% {
    transform: translateX(6px);
  }
  60% {
    transform: translateX(3px);
  }
}

// 1080×1920 设备适配 (竖屏大屏设备)
@media (min-width: 1080px) and (min-height: 1920px) {
  .brands-section {
    padding: $spacing-xxl * 1.5 0;
  }

  .section-title {
    font-size: 2.8rem;

    &::after {
      width: 80px;
      height: 4px;
      margin: $spacing-lg auto;
    }
  }

  .section-subtitle {
    font-size: $font-size-xl;
    max-width: 800px;
  }

  .brands-grid {
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: $spacing-xl;
    padding: $spacing-md;
  }

  .brand-card {
    padding: $spacing-xl;

    &.featured {
      gap: $spacing-xl;
    }
  }

  .brand-logo-container {
    height: 120px;
    margin-bottom: $spacing-lg;
  }

  .brand-logo {
    max-height: 100px;
  }

  .brand-name {
    font-size: $font-size-xl;
  }

  .brand-desc {
    font-size: $font-size-md;
  }

  .brand-stats {
    gap: $spacing-xxl;
    margin-bottom: $spacing-xl;
  }

  .stat-number {
    font-size: $font-size-xxl;
  }

  .stat-label {
    font-size: $font-size-sm;
  }

  .view-all-button {
    padding: 16px 40px;
    font-size: $font-size-md;
  }
}

// 大型桌面设备 (1440px 及以上)
@media (min-width: 1440px) {
  .brands-grid {
    grid-template-columns: repeat(4, 1fr);
  }

  .brand-card.featured {
    grid-column: span 2;
  }
}

// 平板设备 (768px - 1199px)
@media (max-width: 1199px) and (min-width: 768px) {
  .brands-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .brand-card.featured {
    grid-column: span 2;
    flex-direction: column;
    text-align: center;

    .brand-info {
      width: 100%;
    }
  }

  .brand-stats {
    gap: $spacing-lg;
  }
}

// 移动端适配 (767px 及以下)
@media (max-width: 767px) {
  .brands-section {
    padding: $spacing-xl 0;
  }

  .section-header {
    margin-bottom: $spacing-xl;
    padding: 0 $spacing-md;
  }

  .section-title {
    font-size: $font-size-xl;
  }

  .section-subtitle {
    font-size: $font-size-md;
  }

  .brands-container {
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    margin: 0 -16px $spacing-lg;
    padding: 0 16px;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .brands-grid {
    display: flex;
    grid-template-columns: unset;
    gap: $spacing-md;
    padding: $spacing-sm 0;
    width: max-content;
  }

  .brand-card {
    scroll-snap-align: start;
    min-width: 280px;
    opacity: 1;
    animation: none;

    &.featured {
      min-width: 300px;
    }
  }

  .brands-scroll-indicator {
    display: none;
  }

  .section-footer {
    padding: 0 $spacing-md;
  }

  .view-all-button {
    width: 100%;
  }
}

// 超小屏幕设备 (480px 及以下)
@media (max-width: 480px) {
  .section-title {
    font-size: $font-size-lg;
  }

  .section-subtitle {
    font-size: $font-size-sm;
  }

  .brand-card {
    min-width: 260px;
    padding: $spacing-md;

    &.featured {
      min-width: 280px;
    }
  }

  .brand-logo-container {
    height: 80px;
    margin-bottom: $spacing-sm;
  }

  .brand-logo {
    max-height: 60px;
  }

  .brand-name {
    font-size: $font-size-md;
  }

  .brand-desc {
    font-size: $font-size-xs;
    margin-bottom: $spacing-md;
  }

  .brand-stats {
    gap: $spacing-md;
    margin-bottom: $spacing-md;
  }

  .stat-number {
    font-size: $font-size-lg;
  }
}

// 横屏模式适配
@media (max-height: 500px) and (orientation: landscape) {
  .brands-section {
    padding: $spacing-xl 0;
  }

  .brands-grid {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: $spacing-md;
  }

  .brand-card.featured {
    flex-direction: row;

    .brand-info {
      text-align: left;
    }

    .brand-stats {
      justify-content: flex-start;
    }
  }
}

// 暗色模式支持
//@media (prefers-color-scheme: dark) {
//  .brands-section {
//    background: linear-gradient(to bottom, #1e293b 0%, #0f172a 100%);
//  }
//
//  .brand-card {
//    background: #334155;
//  }
//
//  .section-title {
//    color: $white;
//  }
//
//  .section-subtitle {
//    color: rgba($white, 0.7);
//  }
//
//  .brand-name {
//    color: $white;
//  }
//
//  .brand-desc {
//    color: rgba($white, 0.7);
//  }
//
//  .stat-label {
//    color: rgba($white, 0.6);
//  }
//}

// 减少动画（适用于偏好减少动画的用户）
@media (prefers-reduced-motion: reduce) {
  .brand-card,
  .brand-logo,
  .button-arrow {
    transition: none;
  }

  .scroll-arrow {
    animation: none;
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
}
</style>
